import Tracker, { SanitizeLevel } from '@openreplay/tracker'
import trackerAssist from '@openreplay/tracker-assist'

class OpenReplayService {
  constructor() {
    this.tracker = null
    this.isEnabled = process.env.REACT_APP_OPENREPLAY_ENABLED === 'true'
  }

  init() {
    if (!this.isEnabled) {
      return
    }

    const projectKey = process.env.REACT_APP_OPENREPLAY_PROJECT_KEY
    const ingestPoint = process.env.REACT_APP_OPENREPLAY_INGEST_POINT

    if (!projectKey || !ingestPoint) {
      return
    }

    try {
      this.tracker = new Tracker({
        projectKey,
        ingestPoint,
        __DISABLE_SECURE_MODE: process.env.NODE_ENV === 'development',
        respectDoNotTrack: false,
        obscureTextEmails: true,
        obscureTextNumbers: false,
        obscureInputEmails: true,
        obscureInputDates: true,
        capturePerformance: true,
        defaultInputMode: 1,
        network: {
          capturePayload: true,
          ignoreHeaders: ['authorization', 'cookie'],
        },

        domSanitizer: (node) => {
          if (node.tagName === 'VIDEO' || node.tagName === 'AUDIO') {
            return SanitizeLevel.Hidden
          }

          if (node.classList && node.classList.contains('__openreplay-mask')) {
            return SanitizeLevel.Obscured
          }

          if (node.classList && node.classList.contains('__openreplay-block')) {
            return SanitizeLevel.Hidden
          }

          return SanitizeLevel.Plain
        },
      })

      this.tracker.use(
        trackerAssist({
          callConfirm: {
            text: 'Um profissional da Telavita de suporte gostaria de iniciar uma chamada com você. Aceitar?',
            style: {
              backgroundColor: '#ffffff',
              color: '#333333',
            },
            confirmBtn: {
              innerHTML: 'Permitir acesso',
              style: {
                backgroundColor: '#4CAF50',
                color: '#ffffff',
                padding: '10px 20px',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: 'bold',
              },
            },
            declineBtn: {
              innerHTML: 'Recusar',
              style: {
                backgroundColor: '#f44336',
                color: '#ffffff',
                padding: '10px 20px',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: 'bold',
              },
            },
          },
          controlConfirm: {
            text: 'A equipe de Suporte da Telavita pode ajudar a resolver os problemas na sua chamada. Para isso, precisamos de permissão para visualizar e controlar sua tela temporariamente. Deseja permitir?',
            style: {
              backgroundColor: '#ffffff',
              color: '#333333',
            },
            confirmBtn: {
              innerHTML: 'Permitir controle',
              style: {
                backgroundColor: '#4CAF50',
                color: '#ffffff',
                padding: '10px 20px',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: 'bold',
              },
            },
            declineBtn: {
              innerHTML: 'Recusar',
              style: {
                backgroundColor: '#f44336',
                color: '#ffffff',
                padding: '10px 20px',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: 'bold',
              },
            },
          },
          compressionEnabled: true,
          compressionMinBatchSize: 5000,
          onAgentConnect: () => {
            console.log('OpenReplay: Agente conectado para sessão de co-browsing')
            return () => {
              console.log('OpenReplay: Agente desconectado')
            }
          },
          onCallStart: () => {
            console.log('OpenReplay: Chamada de vídeo/áudio iniciada')
            return () => {
              console.log('OpenReplay: Chamada de vídeo/áudio finalizada')
            }
          },
          onRemoteControlStart: () => {
            console.log('OpenReplay: Controle remoto iniciado')
            return () => {
              console.log('OpenReplay: Controle remoto finalizado')
            }
          },
          onCallDeny: () => {
            console.log('OpenReplay: Chamada negada pelo usuário')
            this.tracker?.event('assist_call_denied')
          },
          onRemoteControlDeny: () => {
            console.log('OpenReplay: Controle remoto negado pelo usuário')
            this.tracker?.event('assist_remote_control_denied')
          },
          onRecordingDeny: () => {
            console.log('OpenReplay: Gravação negada pelo usuário')
            this.tracker?.event('assist_recording_denied')
          },
        })
      )

      this.tracker.start()
    } catch (error) {
      console.error('Falha ao inicializar OpenReplay:', error)
    }
  }

  identifyUser(user, metadata = {}) {
    if (!this.tracker || !user) {
      return
    }

    try {
      const userId = user.email || user.username
      if (userId) {
        this.tracker.setUserID(userId)
      }

      if (user.user_uuid) {
        this.tracker.setMetadata('user_uuid', user.user_uuid)
      }
      if (user.profile_code) {
        this.tracker.setMetadata('profile_code', user.profile_code)
      }
      if (user.full_name) {
        this.tracker.setMetadata('full_name', user.full_name)
      }

      Object.entries(metadata).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          this.tracker.setMetadata(key, String(value))
        }
      })
    } catch (error) {
      console.error('Falha ao identificar o usuário no OpenReplay:', error)
    }
  }
}

export const openReplay = new OpenReplayService()